import React, { useCallback } from 'react'
import { ClapperBoardIcon } from '@/atoms/Icons/ClapperBoardIcon'
import { ClockIcon } from '@/atoms/Icons/ClockIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { HandsOutlineIcon } from '@/atoms/Icons/HandsOutlineIcon'
import { CaptionMD, HeadingMD, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../TorchSubmitButton'
import { TorchStepsCard } from './TorchStepsCard'

export const TorchHeroSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')

  const iconSize = innerWidth > 1024 ? 48 : 32

  const handleSubmitClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="w-full py-16 md:py-24"
      style={{
        backgroundImage: 'linear-gradient(130deg, #232323 0%, #141414 100%)',
      }}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-12 lg:px-[120px]">
        {/* Hero Content */}
        <div className="mb-12 text-center">
          <HeadingMD weight="bold" color="white" className="md:photon-heading-xl lg:photon-display mb-6">
            {t('bringStoriesToLifeTitle', 'Bring Stories to Life')}
          </HeadingMD>
          <ParagraphMD weight="medium" color="core-gray-400" className="md:photon-paragraph-lg mx-auto max-w-2xl">
            {t(
              'submitYourProjectWorldWatching',
              'Submit your project. If the Guild loves it, the world could be watching next.',
            )}
          </ParagraphMD>
        </div>

        {/* Step Cards */}
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:gap-8">
          <TorchStepsCard
            icon={<ClapperBoardIcon size={iconSize} color="white" />}
            title={t('oneSubmitProject', '1. Submit Your Project')}
            description={t('submitProjectDetailedDescription', 'Your submission will go through three steps:')}
            descriptionList={[
              t('submissionReview', 'Submission Review - Checked for compliance.'),
              t('defendersVote', 'Defenders Vote - Smaller sample of Guild members choose what advances.'),
              t('generalGuildVote', 'General Guild Vote - A larger sample of Guild members establish the final score.'),
            ]}
          />
          <TorchStepsCard
            icon={<GuildIconMono size={iconSize} color="white" />}
            title={t('receiveGuildScore', '2. Receive a Guild Score')}
            description={[
              t('submissionsScored', 'Submissions are scored on a scale of 0-100.'),
              t(
                'passingDefenders',
                'Passing Guild Defenders requires a score of 50+. This advances your submission to the General Guild and waives the application fee for resubmissions.',
              ),
              t('passingGeneralGuild', 'Passing the General Guild requires a final score of 70+.'),
            ]}
          />
          <TorchStepsCard
            icon={<HandsOutlineIcon size={iconSize} color="white" />}
            title={t('potentialPartnership', '3. Potential Partnership')}
            description={[
              t(
                'submissionsEligible',
                'Submissions with a final Guild score of 70+ are eligible for, but not guaranteed, partnership.',
              ),
              t(
                'angelReviews',
                'Angel reviews each Guild approved submission and considers the Guild score, marketability, team capacity, project stage, and more before extending a contract offer.',
              ),
            ]}
          />
        </div>

        {/* Timing Information */}
        <div className="mb-8 flex items-center justify-center gap-2 pb-8 text-center">
          <ClockIcon size={16} color="core-gray-500" />
          <ParagraphSM weight="normal" color="core-gray-500">
            {t(
              'applicationTimeline',
              "Application Review: 3-7 days | Voting: 1-10 days (longer for kids' submissions)",
            )}
          </ParagraphSM>
        </div>

        {/* CTA Section */}
        <div className="flex flex-col items-center gap-4">
          <TorchSubmitButton handleSubmitClicked={handleSubmitClicked} isDark={true} />
          <CaptionMD weight="normal" color="core-gray-600">
            {t('applicationFee', '$250 application fee')}
          </CaptionMD>
        </div>
      </div>
    </section>
  )
}
